/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { useForm, useFieldArray, Controller, useWatch } from 'react-hook-form';
import { Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { updateDraftInterviewExperience, updateInterviewExperience } from '../../services/operations/interviewExperiences';
import Fuse from 'fuse.js';
import { useSelector } from 'react-redux';
import RichEditor from '../../components/RichEditor/RichEditor';
import { useNavigate } from 'react-router-dom';
const InterviewFormModal = ({ initialData, companies, isDraftMode = false }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnonymous, setIsAnonymous] = useState(initialData?.isAnonymous || false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: initialData || {
      isAnonymous: false,
      personal: {
        workExp: 'Select',
      },
      rounds: [
        {
          name: '',
          type: 'Technical',
          mode: 'Remote',
          difficulty: 'Medium',
          duration: '60',
          overview: '',
          questions: [{ text: 'Linear Search', link: '' }],
        },
      ],
    },
  });

  const [companyQuery, setCompanyQuery] = useState('');
  const [companySuggestions, setCompanySuggestions] = useState(companies || []);
  const [showSuggestions, setShowSuggestions] = useState(false);


  const { fields: roundFields, append: appendRound, remove: removeRound } = useFieldArray({
    control,
    name: 'rounds',
  });

  useEffect(() => {
    if (initialData) {
      reset(initialData)
    }

  }, [reset, initialData])

  const fuse = new Fuse(companies || [], {
    keys: ['name'],
    threshold: 0.3,
    includeScore: true,
    minMatchCharLength: 1,
  });

  const handleCompanySearch = (query) => {
    setCompanyQuery(query);
    if (query.trim() === '') {
      setCompanySuggestions(companies.slice(0, 5));
    } else {
      const results = fuse.search(query);
      setCompanySuggestions(results.map(result => result.item).slice(0, 5));
      setShowSuggestions(true);
    }
  };

  useEffect(() => {
    if (initialData) {
      reset(initialData);
      setCompanyQuery(initialData.company?.name || '');
    }
    // Set initial suggestions
    setCompanySuggestions(companies);
  }, [reset, initialData, companies]);


  const steps = ['Personal', 'Company', 'Rounds', 'Feedback'];

  const onSubmit = (data) => {
    try {
      console.log(initialData.id)
      const finalData = {
        isAnonymous: initialData?.isAnonymous || false,
        personalInfo: data?.personal || {},
        companyDetails: data.company || {},
        interviewRounds: data?.rounds || {},
        overallFeedback: data?.feedback || {},
      };
      
      if(isDraftMode) {
        dispatch(updateDraftInterviewExperience(initialData.id, finalData, navigate));
      } else {
        dispatch(updateInterviewExperience(initialData.id, finalData, isAnonymous, setLoading, navigate));
      }
    } catch (error) {
      console.error('Error submitting form', error);
    }
  };

  return (
    <div className='flex items-center justify-start p-4 min-h-[85vh]'>
      <div className='bg-white dark:bg-dark rounded-2xl max-w-[1200px] w-full p-8  min-h-[75vh] font-dmSans relative'>
        {/* Progress Bar */}
        <div className='w-full h-1 bg-gray-200 dark:bg-gray-700 relative mb-6'>
          <div
            className='h-1 bg-orange-500 absolute top-0 transition-all duration-300'
            style={{ width: `${(currentStep + 1) * 25}%` }}
          ></div>
        </div>

        {/* Navigation Tabs */}
        <div className='flex justify-between text-gray-500 dark:text-gray-400 text-sm mb-6'>
          {steps.map((step, index) => (
            <span
              key={step}
              className={`${currentStep === index ? 'text-orange-500 font-semibold' : ''}`}
            >
              {step} {index === 0 ? 'Information' : 'Details'}
            </span>
          ))}
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit(onSubmit)}>
          {currentStep === 0 && (
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-6'>
              {!isAnonymous && (
                <>
                  <div>
                    <label className='block text-new_primary dark:text-[#FAFAFA] mb-1'>Full Name</label>
                    <input
                      {...register('personal.fullName', { required: !isAnonymous })}
                      className='w-full p-3 border-1 rounded-lg border-new_border dark:border-gray-700 placeholder:text-new_quaternary dark:placeholder:text-[#808080] dark:bg-[#18191B] dark:text-[#FAFAFA]'
                      placeholder='Enter your name'
                    />
                    {errors.personal?.fullName && (
                      <span className='text-red-500 text-sm'>This field is required</span>
                    )}
                  </div>
                  <div>
                    <label className='block text-new_primary dark:text-[#FAFAFA] mb-1'>Email Address</label>
                    <input
                      type='email'
                      {...register('personal.email', { required: !isAnonymous })}
                      className='w-full p-3 border-1 rounded-lg border-new_border dark:border-gray-700 placeholder:text-new_quaternary dark:placeholder:text-[#808080] dark:bg-[#18191B] dark:text-[#FAFAFA]'
                      placeholder='Enter email address'
                    />
                    {errors.personal?.email && (
                      <span className='text-red-500 text-sm'>Valid email is required</span>
                    )}
                  </div>
                </>
              )}
              <div>
                <label className='block text-new_primary dark:text-[#FAFAFA] mb-1'>Work Experience</label>
                <Controller
                  name='personal.workExp'
                  control={control}
                  render={({ field }) => (
                    <select {...field} className='w-full p-3 border-1 border-new_border dark:border-gray-700 rounded-lg text-new_quaternary dark:text-[#808080] dark:bg-[#18191B]'>
                      <option value='Select' disabled>
                        Select
                      </option>
                      <option value="0-1 years">0-1 years</option>
                      <option value="1-2 years">1-2 years</option>
                      <option value="2-3 years">2-3 years</option>
                      <option value="3-4 years">3-4 years</option>
                      <option value="4-5 years">4-5 years</option>
                      <option value="5+ years">5+ years</option>
                    </select>
                  )}
                />
              </div>
              <div>
                <label className='block text-new_primary dark:text-[#FAFAFA] mb-1'>Current Role</label>
                <input
                  {...register('personal.currentRole', { required: true })}
                  className='w-full p-3 border-1 rounded-lg border-new_border dark:border-gray-700 placeholder:text-new_quaternary dark:placeholder:text-[#808080] dark:bg-[#18191B] dark:text-[#FAFAFA]'
                  placeholder='Your current role'
                />
                {errors.personal?.currentRole && (
                  <span className='text-red-500 text-sm'>This field is required</span>
                )}
              </div>
              {!isAnonymous && (
                <div className='sm:col-span-2'>
                  <label className='block text-new_primary dark:text-[#FAFAFA] mb-1'>LinkedIn Profile</label>
                  <input
                    {...register('personal.linkedin')}
                    className='w-full p-3 border-1 rounded-lg border-new_border dark:border-gray-700 placeholder:text-new_quaternary dark:placeholder:text-[#808080] dark:bg-[#18191B] dark:text-[#FAFAFA]'
                    placeholder='Paste your LinkedIn profile link here'
                  />
                </div>
              )}
              <div className="sm:col-span-2">
                <label className="block text-new_primary dark:text-[#FAFAFA] mb-1 text-sm">
                  About yourself <span className="text-red-500">*</span>
                </label>
                <div className="">
                  <Controller
                    name="personal.aboutYourself"
                    control={control}
                    rules={{ required: 'Please tell us about yourself' }}
                    render={({ field }) => (
                      <RichEditor
                        value={field.value}
                        onChange={({ html }) => field.onChange(html)}
                        placeholder="Write about your current role, previous experience, skills, etc. (30-40 words recommended)"
                        height="120px"
                        toolbarOptions="basic"
                        showWordCount={true}
                        options="Notes"
                      />
                    )}
                  />
                  {errors.personal?.aboutYourself && (
                    <span className="text-red-500 text-sm">{errors.personal.aboutYourself.message}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div className='mb-12'>
              <h1 className='font-semibold mb-6 dark:text-[#FAFAFA]'>Company Details</h1>
              <div className='mb-6'>
                <label className='block text-new_primary dark:text-[#FAFAFA] mb-1'>Company Name</label>
                <Controller
                  name='company.name'
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <div className='relative'>
                      <input
                        {...field}
                        value={companyQuery}
                        autoComplete='off'
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          handleCompanySearch(e.target.value);
                        }}
                        onFocus={() => handleCompanySearch(companyQuery)}
                        className='w-full p-3 border-1 rounded-lg border-new_border dark:border-gray-700 placeholder:text-new_quaternary dark:placeholder:text-[#808080] dark:bg-[#18191B] dark:text-[#FAFAFA]'
                        placeholder='Enter Company name'
                      />
                      {showSuggestions && companySuggestions.length > 0 && (
                        <div
                          className='absolute p-2 z-10 border rounded-lg max-h-80 overflow-y-auto shadow-lg w-full bg-white dark:bg-[#18191B] top-full left-0 mt-1'
                        >
                          {companySuggestions.map((suggestion) => (
                            <p
                              key={suggestion.id}
                              onClick={() => {
                                field.onChange(suggestion.name);
                                setCompanyQuery(suggestion.name);
                                setShowSuggestions(false);
                              }}
                              className='p-2 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 dark:text-[#FAFAFA] rounded-md'
                            >
                              {suggestion.name}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                />

                {errors.company?.name && (
                  <span className='text-red-500 text-sm'>Company name is required</span>
                )}
              </div>
              <div className='mb-6'>
                <label className='block text-new_primary dark:text-[#FAFAFA] mb-1'>Job Position Applied</label>
                <input
                  {...register('company.position', { required: true })}
                  className='w-full p-3 border-1 rounded-lg border-new_border dark:border-gray-700 placeholder:text-new_quaternary dark:placeholder:text-[#808080] dark:bg-[#18191B] dark:text-[#FAFAFA]'
                  placeholder='Enter position applied'
                />
                {errors.company?.position && (
                  <span className='text-red-500 text-sm'>Position is required</span>
                )}
              </div>
              <div>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-6'>
                  <div>
                    <label className='block text-new_primary dark:text-[#FAFAFA] mb-1'>Difficulty Level</label>
                    <Controller
                      name='company.difficulty'
                      control={control}
                      render={({ field }) => (
                        <select
                          {...field}
                          className='w-full p-3 border-1 border-new_border dark:border-gray-700 rounded-lg text-new_quaternary dark:text-[#808080] dark:bg-[#18191B]'
                        >
                          <option value=''>Select</option>
                          <option value='Easy'>Easy</option>
                          <option value='Medium'>Medium</option>
                          <option value='Hard'>Hard</option>
                          <option value='Easy-Medium'>Easy-Medium</option>
                          <option value='Medium-Hard'>Medium-Hard</option>
                          <option value='Very Hard'>Very Hard</option>
                        </select>
                      )}
                    />
                  </div>
                  <div>
                    <label className='block text-new_primary dark:text-[#FAFAFA] mb-1'>Interview Timeline</label>
                    <Controller
                      name='company.interviewTimeline'
                      control={control}
                      render={({ field }) => (
                        <select
                          {...field}
                          className='w-full p-3 border-1 border-new_border dark:border-gray-700 rounded-lg text-new_quaternary dark:text-[#808080] dark:bg-[#18191B]'
                        >
                         <option value="">Select Interview Duration</option>
                          <option value="0-1 Weeks">0-1 Weeks</option>
                          <option value="1-2 Weeks">1-2 Weeks</option>
                          <option value="2-3 Weeks">2-3 Weeks</option>
                          <option value="3-4 Weeks">3-4 Weeks</option>
                          <option value="4-5 Weeks">4-5 Weeks</option>
                          <option value="5-6 Weeks">5-6 Weeks</option>
                          <option value="6+ Weeks">6+ Weeks</option>
                        </select>
                      )}
                    />
                  </div>
                  <div>
                    <label className='block text-new_primary dark:text-[#FAFAFA] mb-1'>How did you apply?</label>
                    <Controller
                      name='company.applicationMode'
                      control={control}
                      render={({ field }) => (
                        <select
                          {...field}
                          className='w-full p-3 border-1 border-new_border dark:border-gray-700 rounded-lg text-new_quaternary dark:text-[#808080] dark:bg-[#18191B]'
                        >
                         <option value="">Select</option>
                          <option value="Career Fair">Career Fair</option>
                          <option value="Company Website">Company Website</option>
                          <option value="Email">Email</option>
                          <option value="Job Portal">Job Portal</option>
                          <option value="LinkedIn">LinkedIn</option>
                          <option value="Referral">Referral</option>
                          <option value="Walk-in">Walk-in</option>
                          <option value="Twitter">Twitter</option>
                          <option value="Other">Other</option>
                        </select>
                      )}
                    />
                  </div>
                  <div>
                    <label className='block text-new_primary dark:text-[#FAFAFA] mb-1'>Interview Mode</label>
                    <Controller
                      name='company.interviewMode'
                      control={control}
                      render={({ field }) => (
                        <select
                          {...field}
                          className='w-full p-3 border-1 border-new_border dark:border-gray-700 rounded-lg text-new_quaternary dark:text-[#808080] dark:bg-[#18191B]'
                        >
                          <option value=''>Select</option>
                          <option value='Remote'>Remote</option>
                          <option value='On-site'>On-site</option>
                          <option value='Hybrid'>Hybrid</option>
                          <option value='Telephonic'>Telephonic</option>
                          <option value='Other'>Other</option>
                        </select>
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className='space-y-6 mb-20'>
              <div className='flex justify-between items-center'>
                <h3 className='text-lg font-semibold dark:text-[#FAFAFA]'>Interview Rounds</h3>
                <button
                  type='button'
                  onClick={() =>
                    appendRound({
                      name: '',
                      type: 'Technical',
                      mode: 'Remote',
                      difficulty: 'Medium',
                      duration: '60',
                      overview: '',
                      questions: [{ text: '' }],
                    })
                  }
                  className='bg-orange-500 text-white px-4 py-2 rounded-md'
                >
                  Add Round
                </button>
              </div>

              {roundFields.map((round, roundIndex) => (
                <RoundSection
                  key={round.id}
                  control={control}
                  register={register}
                  errors={errors}
                  roundIndex={roundIndex}
                  removeRound={removeRound}
                  setValue={setValue}
                />
              ))}
            </div>
          )}

          {currentStep === 3 && (
            <div className='space-y-6 mb-10'>
              <div>
                <label className='block text-new_primary dark:text-[#FAFAFA] mb-1'>Interview Result</label>
                <Controller
                  name='feedback.result'
                  control={control}
                  render={({ field }) => (
                    <select {...field} className='w-full p-3 border-1 border-new_border dark:border-gray-700 rounded-lg dark:bg-[#18191B] dark:text-[#FAFAFA]'>
                      <option value=''>Select</option>
                      <option value='Selected'>Selected</option>
                      <option value='Rejected'>Rejected</option>
                      <option value='Ghosted'>Ghosted</option>
                    </select>
                  )}
                />
              </div>

              <div>
                <label className='block text-new_primary dark:text-[#FAFAFA] mb-1'>Salary Range (LPA)</label>
                <input
                  {...register('feedback.salaryRange')}
                  className='w-full p-3 border-1 border-new_border dark:border-gray-700 rounded-lg dark:bg-[#18191B] dark:text-[#FAFAFA] dark:placeholder:text-[#808080]'
                  placeholder='Eg 8-10 or N/A (Not Applicable)'
                  list="salaryOptions"
                  autoComplete='off'
                />
                <datalist id="salaryOptions">
                  <option value="N/A (Not Applicable)"></option>
                </datalist>
              </div>

              <div>
                <label className='block text-new_primary dark:text-[#FAFAFA] mb-1'>Preparation Tips <span className="text-red-500 text-sm ml-1">(Warning: Please don't use color formatting as it may cause issues in light/dark themes)</span></label>
                <Controller
                  name='feedback.preparationTips'
                  control={control}
                  render={({ field }) => (
                    <RichEditor
                      value={field.value}
                      onChange={(data) => field.onChange(data.html)}
                      options="Notes"
                    />
                  )}
                />
              </div>

              <div>
                <label className='block text-new_primary dark:text-[#FAFAFA] mb-1'>General Advice <span className="text-red-500 text-sm ml-1">(Warning: Please don't use color formatting as it may cause issues in light/dark themes)</span></label>
                <Controller
                  name='feedback.generalAdvice'
                  control={control}
                  render={({ field }) => (
                    <RichEditor
                      value={field.value}
                      onChange={(data) => field.onChange(data.html)}
                      options="Notes"
                    />
                  )}
                />
              </div>
            </div>
          )}

          <div className='absolute flex gap-4 justify-end mt-6 right-10 bottom-4'>
            {currentStep > 0 && (
              <button
                type='button'
                onClick={() => setCurrentStep((prev) => prev - 1)}
                className='px-6 sm:px-10 py-3 bg-[#F47B54] text-white rounded-md hover:bg-[#e06642]'
              >
                Previous
              </button>
            )}

            {currentStep <= 2 && (
              <button
                type='button'
                onClick={() => setCurrentStep((prev) => prev + 1)}
                className='px-6 sm:px-10 py-3 bg-[#F47B54] text-white rounded-md hover:bg-[#e06642]'
              >
                Next
              </button>
            )}
            {currentStep === 3 && (
              <button
                type='submit'
                className='px-6 sm:px-10 py-3 bg-[#F47B54] text-white rounded-md hover:bg-[#e06642]'
              >
                {loading ? "Updating..." : "Update"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};
const RoundSection = ({ control, register, errors, roundIndex, removeRound, setValue }) => {
  const { AllDSAProblems } = useSelector((state) => state.new_syllabus);
  const [isExpanded, setIsExpanded] = useState(false);
  const { fields: questionFields, append: appendQuestion, remove: removeQuestion } = useFieldArray({
    control,
    name: `rounds.${roundIndex}.questions`,
  });

  const roundName = useWatch({
    control,
    name: `rounds.${roundIndex}.name`,
    defaultValue: `Round ${roundIndex + 1}`
  });

  const [questionQueries, setQuestionQueries] = useState(
    questionFields.map(() => '')
  );
  const [showSuggestions, setShowSuggestions] = useState(
    questionFields.map(() => false)
  );

  const fuse = new Fuse(AllDSAProblems || [], {
    keys: ['problem_name'],
    threshold: 0.3,
    includeScore: true,
    minMatchCharLength: 1,
  });

  const generateLink = (slug) => {
    return `https://takeuforward.org/plus/dsa/problems/${slug}`;
  };

  const handleQuestionSearch = (query, questionIndex) => {
    const updatedQueries = [...questionQueries];
    updatedQueries[questionIndex] = query;
    setQuestionQueries(updatedQueries);

    const updatedShowSuggestions = [...showSuggestions];
    updatedShowSuggestions[questionIndex] = true;
    setShowSuggestions(updatedShowSuggestions);
  };

  const handleSuggestionSelect = (suggestion, questionIndex, field) => {
    field.onChange(suggestion.problem_name);
    setValue(
      `rounds.${roundIndex}.questions.${questionIndex}.link`,
      generateLink(suggestion.slug)
    );

    const updatedQueries = [...questionQueries];
    updatedQueries[questionIndex] = suggestion.problem_name;
    setQuestionQueries(updatedQueries);

    const updatedShowSuggestions = [...showSuggestions];
    updatedShowSuggestions[questionIndex] = false;
    setShowSuggestions(updatedShowSuggestions);
  };

  useEffect(() => {
    // Sync questionQueries and showSuggestions with questionFields length
    setQuestionQueries(prev => {
      const newQueries = [...prev];
      while (newQueries.length < questionFields.length) newQueries.push('');
      while (newQueries.length > questionFields.length) newQueries.pop();
      return newQueries;
    });
    setShowSuggestions(prev => {
      const newSuggestions = [...prev];
      while (newSuggestions.length < questionFields.length) newSuggestions.push(false);
      while (newSuggestions.length > questionFields.length) newSuggestions.pop();
      return newSuggestions;
    });
  }, [questionFields.length]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className='border p-4 rounded-lg space-y-4 dark:border-gray-700 dark:bg-[#18191B]'>
      <div className='flex justify-between items-center cursor-pointer' onClick={toggleExpand}>
        <div className="flex items-center gap-2 cursor-pointer" onClick={toggleExpand}>  
          <h4 className='font-medium dark:text-[#FAFAFA]'>Round {roundIndex + 1} {roundName ? `- ${roundName}` : ''}</h4>
        </div>
        <div className="flex items-center gap-4">
          <button
            type='button'
            onClick={toggleExpand}
            className='text-orange-500 px-3 py-1 rounded-md border border-orange-500 text-sm'
          >
           {isExpanded ? <ChevronUp size={20} className="text-orange-500" /> : <ChevronDown size={20} className="text-orange-500" />}
          </button>
          <button
            type='button'
            onClick={() => removeRound(roundIndex)}
            className='text-red-500 text-sm'
          >
            <Trash2 className='text-orange-500' size={24} />
          </button>
        </div>
      </div>

      {isExpanded && (
        <>
          <div>
            <label className='block text-new_primary dark:text-[#FAFAFA] mb-1'>Round Name</label>
            <input
              {...register(`rounds.${roundIndex}.name`, { required: true })}
              className='w-full p-2 border-1 border-new_border dark:border-gray-700 rounded-lg dark:bg-[#18191B] dark:text-[#FAFAFA] dark:placeholder:text-[#808080]'
              placeholder='Eg. Technical Round 1'
            />
            {errors.rounds?.[roundIndex]?.name && (
              <span className='text-red-500 text-sm'>Round name is required</span>
            )}
          </div>

          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <div>
              <label className='block text-new_primary dark:text-[#FAFAFA] mb-1'>Round Type</label>
              <Controller
                name={`rounds.${roundIndex}.type`}
                control={control}
                render={({ field }) => (
                  <select {...field} className='w-full p-3 border-1 border-new_border dark:border-gray-700 rounded-lg dark:bg-[#18191B] dark:text-[#FAFAFA]'>
                      <option value="Technical">Technical</option>
                      <option value="HR">HR</option>
                      <option value="Managerial">Managerial</option>
                      <option value="Behavioral">Behavioral</option>
                      <option value="Case Study">Case Study</option>
                      <option value="System Design">System Design</option>
                      <option value="Coding Test">Coding Test</option>
                      <option value="Aptitude Test">Aptitude Test</option>
                      <option value="Group Discussion">Group Discussion</option>
                      <option value="Cultural Fit">Cultural Fit</option>
                      <option value="Final Interview">Final Interview</option>
                      <option value="Executive/Leadership">Executive/Leadership</option>
                      <option value="Offer Discussion">Offer Discussion</option>
                      <option value="Other">Other</option>
                  </select>
                )}
              />
            </div>
            <div>
              <label className='block text-new_primary dark:text-[#FAFAFA] mb-1'>Mode</label>
              <Controller
                name={`rounds.${roundIndex}.mode`}
                control={control}
                render={({ field }) => (
                  <select {...field} className='w-full p-3 border-1 border-new_border dark:border-gray-700 rounded-lg dark:bg-[#18191B] dark:text-[#FAFAFA]'>
                    <option value='Remote'>Remote</option>
                    <option value='On-site'>On-site</option>
                    <option value='Hybrid'>Hybrid</option>
                    <option value='Telephonic'>Telephonic</option>
                    <option value='Other'>Other</option>
                  </select>
                )}
              />
            </div>
            <div>
              <label className='block text-new_primary dark:text-[#FAFAFA] mb-1'>Difficulty Level</label>
              <Controller
                name={`rounds.${roundIndex}.difficulty`}
                control={control}
                render={({ field }) => (
                  <select {...field} className='w-full p-3 border-1 border-new_border dark:border-gray-700 rounded-lg dark:bg-[#18191B] dark:text-[#FAFAFA]'>
                      <option value=''>Select</option>
                      <option value='Easy'>Easy</option>
                      <option value='Medium'>Medium</option>
                      <option value='Hard'>Hard</option>
                      <option value='Easy-Medium'>Easy-Medium</option>
                      <option value='Medium-Hard'>Medium-Hard</option>
                      <option value='Very Hard'>Very Hard</option>
                  </select>
                )}
              />
            </div>
            <div>
              <label className='block text-new_primary dark:text-[#FAFAFA] mb-1'>Duration (mins)</label>
              <input
                type='number'
                {...register(`rounds.${roundIndex}.duration`, { required: true })}
                className='w-full p-3 border-1 border-new_border dark:border-gray-700 rounded-lg dark:bg-[#18191B] dark:text-[#FAFAFA]'
              />
              {errors.rounds?.[roundIndex]?.duration && (
                <span className='text-red-500 text-sm'>Duration is required</span>
              )}
            </div>
          </div>

          <div className='overview'>
            <label className='block text-new_primary dark:text-[#FAFAFA] mb-1'>Round Overview <span className="text-red-500 text-sm ml-1">(Warning: Please don't use color formatting as it may cause issues in light/dark themes)</span></label>
            <Controller
              name={`rounds.${roundIndex}.overview`}
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <RichEditor
                  value={field.value}
                  onChange={(data) => field.onChange(data.html)}
                  options="Notes"
                />
              )}
            />
            {errors.rounds?.[roundIndex]?.overview && (
              <span className='text-red-500 text-sm'>Overview is required</span>
            )}
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <div className='flex items-center gap-2'>
                <h5 className='text-new_primary dark:text-[#FAFAFA]'>Questions</h5>
                <span className="text-xs text-orange-500 dark:text-orange-500">( You can search for problems or add questions manually )</span>
              </div>
              <button
                type='button'
                onClick={() => appendQuestion({ text: '', link: '' })}
                className='bg-orange-500 text-white px-4 py-2 text-sm rounded-md whitespace-nowrap'
              >
                Add Question
              </button>
            </div>
            
            {questionFields.map((question, questionIndex) => (
              <div key={question.id} className='flex items-center space-x-2 mb-2'>
                <Controller
                  name={`rounds.${roundIndex}.questions.${questionIndex}.text`}
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <div className='relative w-full'>
                      <input
                        {...field}
                        value={questionQueries[questionIndex] || field.value || ''}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          handleQuestionSearch(e.target.value, questionIndex);
                          setValue(`rounds.${roundIndex}.questions.${questionIndex}.link`, ''); // Clear link on manual edit
                        }}
                        onFocus={() => handleQuestionSearch(questionQueries[questionIndex] || field.value || '', questionIndex)}
                        className='w-full p-2 border-1 border-new_border dark:border-gray-700 rounded-lg dark:bg-[#18191B] dark:text-[#FAFAFA] dark:placeholder:text-[#808080]'
                        placeholder='Enter or search for a DSA problem'
                        autoComplete='off'
                      />
                      {showSuggestions[questionIndex] && 
                       questionQueries[questionIndex] && 
                       fuse.search(questionQueries[questionIndex]).slice(0, 5).length > 0 && (
                        <ul className='absolute z-10 border rounded-lg max-h-60 overflow-y-auto shadow-lg w-full bg-white dark:bg-[#18191B] top-full left-0 mt-1'>
                          {fuse.search(questionQueries[questionIndex]).slice(0, 5).map((result) => (
                            <li
                              key={result.item.id}
                              onClick={() => handleSuggestionSelect(result.item, questionIndex, field)}
                              className='p-2 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 dark:text-[#FAFAFA] rounded-md'
                            >
                              {result.item.problem_name}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                />
                {errors.rounds?.[roundIndex]?.questions?.[questionIndex]?.text && (
                  <span className='text-red-500 text-sm'>Question is required</span>
                )}

                <input
                  {...register(`rounds.${roundIndex}.questions.${questionIndex}.link`, { required: true })}
                  className='w-full p-2 border-1 border-new_border dark:border-gray-700 rounded-lg dark:bg-[#18191B] dark:text-[#FAFAFA] dark:placeholder:text-[#808080]'
                  placeholder='Link will be auto-generated for DSA problems or you can add your own link'
                />
                {errors.rounds?.[roundIndex]?.questions?.[questionIndex]?.link && (
                  <span className='text-red-500 text-sm'>Link is required</span>
                )}

                <button
                  type='button'
                  onClick={() => removeQuestion(questionIndex)}
                  className='text-red-500'
                >
                  <Trash2 size={20} />
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default InterviewFormModal;
